import { Archive, ArchiveRestore, Pencil } from "lucide-react"
import { useState } from "react"
import type { ReactNode } from "react"
import { Markdown } from "@holigral/shared/ui"
import { H1, H2 } from "@holigral/shared/ui/components"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import { Button } from "@holigral/shared/ui/components/ui/button"
import BriefEditDialog from "./BriefEditDialog"
import useToggleArchived from "./useToggleArchived"
import { useGetBriefQuery } from "./api"
import { useT } from "@/i18n"
import { LoadingScreen } from "@/ui"
import type { Uuid } from "@/utils"

type Props = {
  readonly briefId: Uuid
  /**
   * The brief's assignments table, composed by the route — a direct
   * import of @/assignment here would create a barrel cycle
   */
  readonly assignments?: ReactNode
}

export default function BriefDetailPage({ briefId, assignments }: Props) {
  const { t } = useT("brief")
  const [editOpen, setEditOpen] = useState(false)
  const { data: brief, isLoading } = useGetBriefQuery(briefId)
  const toggleArchived = useToggleArchived()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!brief) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
        <p className="text-muted-foreground">{t("notFound")}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <H1>{brief.name}</H1>
          {brief.archived && (
            <Badge variant="pending">{t("archivedBadge")}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setEditOpen(true)
            }}
          >
            <Pencil />
            {t("edit")}
          </Button>
          <Button variant="outline" onClick={() => toggleArchived(brief)}>
            {brief.archived ? <ArchiveRestore /> : <Archive />}
            {brief.archived ? t("unarchive") : t("archive")}
          </Button>
        </div>
      </div>
      {brief.description ? (
        <Markdown>{brief.description}</Markdown>
      ) : (
        <p className="text-muted-foreground">{t("noDescription")}</p>
      )}
      {assignments ? (
        <section className="flex flex-col gap-4">
          <H2>{t("assignmentsTitle")}</H2>
          {assignments}
        </section>
      ) : null}
      <BriefEditDialog
        brief={brief}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </div>
  )
}
